import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Star } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CountUp } from "@/components/CountUp";
import { InstagramFeed } from "@/components/InstagramFeed";
import { MagneticButton } from "@/components/MagneticButton";
import { SplitText, GradientWord } from "@/components/liquid/SplitText";
import { VelocityMarquee } from "@/components/liquid/VelocityMarquee";
import { MagneticCard } from "@/components/liquid/MagneticCard";
import { ZoomParallax } from "@/components/liquid/ZoomParallax";
import { VideoBackdrop } from "@/components/liquid/VideoBackdrop";
import { ParallaxY } from "@/components/liquid/ParallaxY";
import { pageVideo } from "@/lib/videos";
import { useContent } from "@/context/ContentContext";
import { useBooking } from "@/context/BookingContext";

const HERO_IMG =
  "/api/files/royal-skin-clinic/uploads/3e7a8338-39cc-486b-9b49-9ebf1c838fb7.webp";

export default function HomeLiquid() {
  const { content } = useContent();
  const { book } = useBooking();
  const s = content?.settings || {};
  const vis = (k) => s.section_visibility?.[k] ?? true;
  const allTreatments = content?.treatments || [];
  const featured = (allTreatments.filter((t) => t.featured).length
    ? allTreatments.filter((t) => t.featured)
    : allTreatments
  ).slice(0, 6);
  const doctors = (content?.doctors || []).slice(0, 1);
  const testimonials = (content?.testimonials || []).slice(0, 4);
  const gallery = (content?.gallery || []).slice(0, 4);

  const [ti, setTi] = useState(0);
  useEffect(() => {
    if (!testimonials.length) return;
    const id = setInterval(() => setTi((i) => (i + 1) % testimonials.length), 5000);
    return () => clearInterval(id);
  }, [testimonials.length]);
  const currentTestimonial = testimonials[ti];

  const title = s.hero_title || "Skin, Perfected.";
  const words = title.split(" ").filter(Boolean);
  const leadWords = words.slice(0, -1).join(" ");
  const lastWord = words[words.length - 1];

  const stats = [
    { value: s.stat_years || "10+", label: "Years of Excellence" },
    { value: s.stat_patients || "10,000+", label: "Glowing Patients" },
    { value: s.stat_satisfaction || "99.4%", label: "Satisfaction Rate" },
  ];

  return (
    <div className="relative z-[1]" data-testid="liquid-home">
      {/* HERO — editorial magazine */}
      <section className="relative flex min-h-screen items-center overflow-hidden" data-testid="liquid-hero">
        <VideoBackdrop src={pageVideo(s, "home")} height="h-full" testid="liquid-hero-video" />
        <p aria-hidden="true" className="text-outline pointer-events-none absolute -bottom-6 left-0 select-none font-display text-[22vw] leading-none">
          GLOW
        </p>
        <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-12 px-4 pb-24 pt-44 sm:px-8 lg:grid-cols-12 lg:px-16 lg:pt-40">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
              className="text-[11px] font-medium uppercase tracking-[0.4em] text-[#1A1A1A]/50"
              data-testid="liquid-hero-eyebrow"
            >
              {s.hero_eyebrow || "Dubai Healthcare City · Aesthetic Dermatology"}
            </motion.p>
            <h1 className="mt-8 font-display text-[clamp(3.8rem,11vw,9.5rem)] font-normal leading-[0.92] tracking-tight text-[#1A1A1A]">
              <SplitText text={leadWords} delay={0.35} testid="liquid-hero-title" />
              <br />
              <GradientWord text={lastWord} delay={0.35 + leadWords.length * 0.035 + 0.15} />
            </h1>
            <Reveal delay={0.9}>
              <p
                data-testid="liquid-hero-subtitle"
                className="relative z-20 -mt-4 max-w-md pl-16 text-sm font-light leading-relaxed tracking-wide text-[#1A1A1A]/60 sm:-mt-6 sm:pl-24 sm:text-base"
              >
                {s.hero_subtitle ||
                  "Physician-led skin, hair and wellness artistry in Dubai Healthcare City — precise, discreet, unmistakably you."}
              </p>
            </Reveal>
            <Reveal delay={1.05}>
              <div className="mt-12 flex flex-wrap items-center gap-7">
                <MagneticButton onClick={() => book()} testid="liquid-book-button" className="btn-gold px-10 py-4 text-sm">
                  Book Your Consultation <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </MagneticButton>
                <Link to="/treatments" data-testid="liquid-explore-link" className="group text-sm font-medium text-[#1A1A1A]">
                  <span className="border-b border-[#D4AF37]/50 pb-1 transition-colors duration-300 group-hover:border-[#D4AF37]">
                    Explore the Treatment Menu
                  </span>
                </Link>
              </div>
            </Reveal>
            <Reveal delay={1.2}>
              <div className="mt-16 flex max-w-xl flex-wrap gap-y-6" data-testid="liquid-hero-stats">
                {stats.map((st, i) => (
                  <div key={st.label} className={`pr-10 ${i > 0 ? "border-l border-[#1A1A1A]/10 pl-10" : ""}`}>
                    <CountUp value={st.value} className="block font-display text-3xl font-normal text-[#1A1A1A] sm:text-4xl" />
                    <p className="mt-1 text-[10px] uppercase tracking-[0.25em] text-[#1A1A1A]/45">{st.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
            >
              <ParallaxY speed={0.45}>
                <ZoomParallax src={HERO_IMG} alt="Luxury skin treatment at Royal Skin Cosmetic Clinic" className="h-[52vh] rounded-[4px] shadow-[0_40px_90px_rgba(26,26,26,0.18)] lg:h-[68vh]" />
              </ParallaxY>
              <p className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-[#1A1A1A]/40">
                <span>Fig. 01 — The Treatment Room</span>
                <span>Scroll ↓</span>
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VELOCITY MARQUEE */}
      {vis("home_marquee") && <VelocityMarquee items={allTreatments.slice(0, 10).map((t) => t.name)} />}

      {/* TREATMENTS — magnetic bento */}
      {vis("home_treatments") && (
        <section className="section-pad bg-[#FFFDF9]/80 backdrop-blur-sm">
          <div className="mx-auto max-w-6xl">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <Reveal><p className="eyebrow">The Treatment Menu</p></Reveal>
                <Reveal delay={0.1}>
                  <h2 className="mt-3 font-display text-5xl font-normal text-[#1A1A1A] sm:text-6xl">
                    <SplitText text="Chosen for You" inView stagger={0.02} />
                  </h2>
                </Reveal>
              </div>
              <Reveal delay={0.2}>
                <Link to="/treatments" data-testid="liquid-all-treatments-link" className="group inline-flex items-center gap-2 text-sm font-medium text-[#B38B2D]">
                  View all {allTreatments.length} treatments
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
              </Reveal>
            </div>
            <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="liquid-treatments-grid">
              {featured.map((t, i) => (
                <Reveal key={t.id} delay={(i % 3) * 0.1}>
                  <div data-aura="true">
                    <MagneticCard>
                      <article className="glass group flex h-full flex-col overflow-hidden rounded-[4px] shadow-[0_20px_50px_rgba(26,26,26,0.08)]" data-testid={`liquid-treatment-card-${t.id}`}>
                        <ZoomParallax src={t.image} alt={t.name} className="h-56" />
                        <div className="flex flex-1 flex-col p-7">
                          <p className="lc-lift text-[10px] font-medium uppercase tracking-[0.25em] text-[#B38B2D]">{t.category}</p>
                          <h3 className="lc-lift mt-2 font-display text-3xl font-normal text-[#1A1A1A]">{t.name}</h3>
                          <p className="lc-lift mt-2 text-sm font-light leading-relaxed text-[#5A504B]">{t.tagline}</p>
                          <div className="lc-reveal mt-auto flex items-center justify-between pt-6">
                            <span className="font-display text-2xl text-[#1A1A1A]">AED {t.price}</span>
                            <button
                              onClick={() => book(t.name)}
                              data-testid={`liquid-treatment-book-${t.id}`}
                              aria-label={`Book ${t.name}`}
                              className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1A1A1A] text-[#F3E5AB] transition-colors duration-300 hover:bg-[#D4AF37] hover:text-[#1A1A1A]"
                            >
                              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </article>
                    </MagneticCard>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DOCTOR — editorial split */}
      {vis("home_doctors") && doctors.length > 0 && (
        <section className="section-pad bg-[#F5EBE6]/60 backdrop-blur-sm">
          <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <ZoomParallax
                  src={doctors[0].image}
                  alt={`Portrait of ${doctors[0].name}`}
                  className="h-[60vh] rounded-[4px] shadow-[0_40px_90px_rgba(26,26,26,0.15)]"
                />
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal><p className="eyebrow">{doctors[0].role}</p></Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-4 font-display text-5xl font-normal leading-[1.02] text-[#1A1A1A] sm:text-6xl">
                  <SplitText text={doctors[0].name} inView stagger={0.025} />
                </h2>
              </Reveal>
              <Reveal delay={0.25}>
                <p className="mt-7 max-w-xl text-base font-light leading-relaxed text-[#5A504B]">{doctors[0].bio}</p>
              </Reveal>
              <Reveal delay={0.35}>
                <div className="mt-7 flex flex-wrap gap-2">
                  {(doctors[0].specializations || []).slice(0, 4).map((sp) => (
                    <span key={sp} className="border border-[#1A1A1A]/15 px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-[#1A1A1A]/60">
                      {sp}
                    </span>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.45}>
                <Link to="/doctors" data-testid="liquid-doctor-link" className="btn-gold mt-9 inline-flex px-8 py-3.5 text-sm">
                  Meet {doctors[0].name}
                </Link>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* GALLERY STRIP */}
      {gallery.length > 0 && vis("home_gallery") && (
        <section className="section-pad">
          <div className="mx-auto max-w-6xl">
            <Reveal><p className="eyebrow">Selected Results</p></Reveal>
            <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="liquid-gallery-grid">
              {gallery.map((g, i) => (
                <Reveal key={g.id} delay={i * 0.08}>
                  <Link to="/gallery" data-testid={`liquid-gallery-item-${g.id}`} className="group block">
                    <ZoomParallax src={g.image} alt={g.title} className="aspect-[3/4] rounded-[4px]" />
                    <p className="mt-3 text-[10px] font-medium uppercase tracking-[0.2em] text-[#1A1A1A]/50 transition-colors group-hover:text-[#B38B2D]">
                      {g.title}
                    </p>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIAL — oversized editorial quote */}
      {vis("home_testimonials") && currentTestimonial && (
        <section className="section-pad bg-[#FFFDF9]/80 backdrop-blur-sm">
          <div className="mx-auto max-w-4xl text-center">
            <Reveal><p className="eyebrow">Client Love</p></Reveal>
            <div className="relative mt-10 min-h-[300px] sm:min-h-[240px]" data-testid="liquid-testimonials">
              <AnimatePresence mode="wait">
                <motion.figure
                  key={currentTestimonial.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -24 }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-x-0"
                  data-testid={`liquid-testimonial-${currentTestimonial.id}`}
                >
                  <div className="flex justify-center gap-1">
                    {[...Array(currentTestimonial.rating || 5)].map((_, s2) => (
                      <Star key={s2} className="h-4 w-4 fill-[#D4AF37] text-[#D4AF37]" aria-hidden="true" />
                    ))}
                  </div>
                  <blockquote className="mt-7 font-display text-2xl font-normal italic leading-snug text-[#1A1A1A] sm:text-4xl">
                    “{currentTestimonial.text}”
                  </blockquote>
                  <figcaption className="mt-7">
                    <p className="text-sm font-semibold text-[#1A1A1A]">{currentTestimonial.name}</p>
                    <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[#1A1A1A]/45">{currentTestimonial.treatment}</p>
                  </figcaption>
                </motion.figure>
              </AnimatePresence>
            </div>
            <div className="mt-8 flex justify-center gap-2.5">
              {testimonials.map((t, i) => (
                <button
                  key={t.id}
                  onClick={() => setTi(i)}
                  aria-label={`Show testimonial from ${t.name}`}
                  data-testid={`liquid-testimonial-dot-${i}`}
                  className={`h-2 rounded-full transition-all duration-500 ease-liquid ${ti === i ? "w-8 bg-[#D4AF37]" : "w-2 bg-[#1A1A1A]/15 hover:bg-[#D4AF37]/50"}`}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* INSTAGRAM */}
      <InstagramFeed />

      {/* CTA — oversized editorial */}
      {vis("home_cta") && (
        <section className="section-pad">
          <div className="mx-auto max-w-6xl text-center">
            <Reveal><p className="eyebrow">Begin Today</p></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-6xl font-normal leading-[0.95] text-[#1A1A1A] sm:text-8xl" data-testid="liquid-cta-title">
                <SplitText text="Your skin," inView />
                <br />
                <GradientWord text="perfected." inView delay={0.25} />
              </h2>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mx-auto mt-7 max-w-md text-sm font-light leading-relaxed text-[#5A504B]">
                Consultations with Dr. Javeria Atif at Dubai Healthcare City — discreet, unhurried, unforgettable.
              </p>
            </Reveal>
            <Reveal delay={0.4}>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-5">
                <MagneticButton onClick={() => book()} testid="liquid-cta-book-button" className="btn-gold px-10 py-4 text-sm">
                  Book Your Consultation
                </MagneticButton>
                {s.phone && (
                  <a href={`tel:${s.phone.replace(/[^+\d]/g, "")}`} data-testid="liquid-cta-call" className="btn-ghost-lux px-8 py-4 text-sm">
                    Call {s.phone}
                  </a>
                )}
              </div>
            </Reveal>
          </div>
        </section>
      )}
    </div>
  );
}
